import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { GenesysChatConfig } from '../../models/configurator-integrations.model';
import { getUserByIdGetData } from '@store/swagger/applicationUtilities';
import { filter } from 'rxjs/operators';
import { User } from '@core/models/swagger';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';

const genesysJsValue = 'Genesys' as const;
declare global {
  interface Window {
    _genesysJs: typeof genesysJsValue;
    [genesysJsValue]: any;
    isChatOpened: boolean;
  }
}

export type MsgType = 'system' | 'agent' | 'user' | 'state';

export interface ChatMessage {
  type: MsgType;
  text: string;
  time: string;
  agentName?: string;
  originatingEntity?: string;
  direction: string;
}

export interface MessagesReceivedEvent {
  data: {
    messages: ChatMessage[];
  };
}

export interface typingAgentEvent {
  typing: {
    type: 'On';
    durationMs: 5000;
  };
}

interface GenesysMessage {
  id: string;
  type: string;
  text: string;
  sender: {
    id: string;
    type: 'User' | 'Agent';
    displayName?: string;
  };
  timestamp: string;
}
export interface TypingReceivedEvent {
  data: {
    typing: {
      type: 'On' | 'Off';
      durationMs: number;
    };
  };
}

interface OldMessagesPayload {
  data: GenesysMessage[];
}

export interface MessagesSendEvent {
  message: {
    type: string;
    tracingId: string;
    text: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class GenesysChatService {
  protected readonly store: Store<AppState>;
  protected readonly translateService: TranslateService;
  private readonly baseScriptUrl = 'https://apps.mypurecloud.de';
  chatActive: boolean;

  private customChatAttributes: ChatCustomAttribute | undefined;

  private readonly _messages$ = new BehaviorSubject<ChatMessage[]>([]);
  messages$ = this._messages$.asObservable();

  private readonly _readyFLG$ = new BehaviorSubject<boolean>(false);
  private readonly _startedFLG$ = new BehaviorSubject<boolean>(false);
  private readonly _isTyping$ = new BehaviorSubject<boolean>(false);
  private readonly _isLoading$ = new BehaviorSubject<boolean>(false);

  readyFLG$ = this._readyFLG$.asObservable();
  startedFLG$ = this._startedFLG$.asObservable();
  isTyping$ = this._isTyping$.asObservable();
  isLoading$ = this._isLoading$.asObservable(); 

  isChatStarted = false;
  isChatReady = false;

  private typingTimer: any;

  constructor() {
    this.store = inject(Store);
    this.translateService = inject(TranslateService);

    this.startedFLG$.subscribe(isStarted => {
      this.isChatStarted = isStarted;
      if (!environment.production) {
        console.log('Chat has started in the service:', isStarted);
      }
    }),
      this.readyFLG$.subscribe(isReady => {
        this.isChatReady = isReady;
        if (!environment.production) {
          console.log('Chat has ready obs:', isReady);
        }
      })
  }

  private setChatAttributes(): void {
    window.Genesys('command', 'Database.set', {
      messaging: {
        customAttributes: this.customChatAttributes,
      },
    });
  }

  setGenesysChatConfig(config: GenesysChatConfig | undefined, userNameInserted: string): void {
    this.customChatAttributes = {
      queue: config.queue,
      name: userNameInserted,
    };
    this.setChatAttributes();
  }

  loadGenesysChat(config: GenesysChatConfig | undefined): void {
    if (this.chatActive || !config) {
      return;
    }
    try {
      // Script load
      window._genesysJs = 'Genesys';
      window.Genesys =
        window.Genesys ||
        function (): void {
          (window.Genesys.q = window.Genesys.q || []).push(arguments);
        };
      window.Genesys.t = 1 * new Date().getTime();

      window.Genesys.c = {
        environment: config.environment,
        deploymentId: config.deploymentId,
      };
      const ys = document.createElement('script');
      ys.async = true;
      ys.src = `${this.baseScriptUrl}/genesys-bootstrap/genesys.min.js`;
      ys.charset = 'utf-8';
      document.head.appendChild(ys);
      this.setupChat();
      this.chatActive = true;
    } catch (e) {
      console.log(e);
      this.chatActive = false;
    }
  }

  private setupChat(): void {
    window.Genesys('subscribe', 'MessagingService.ready', () => {
      if (!environment.production) {
        console.log('MessagingService.ready');
      }

      this._readyFLG$.next(true);
      this.startConversation();
    });
    window.Genesys('subscribe', 'MessagingService.started', async () => {
      if (!environment.production) {
        console.log('MessagingService.started');
      }
      this._startedFLG$.next(true);
      let initChatLabel = await lastValueFrom(this.translateService.get('GENESYS_CHAT.MESSAGES.CHAT.START'));
      this.pushState(await initChatLabel);
    });
    window.Genesys('subscribe', 'MessagingService.messagesReceived', ({ data }: MessagesReceivedEvent) => {
      if (!environment.production) {
        console.log('messagesReceived', data);
      }

      data.messages.forEach((message: ChatMessage) => {
        if (message.direction === 'Outbound') {
          if (!environment.production) {
            console.log('Mensaje de salida (Outbound):', message.text);
            console.log('message.type', message.type);
            console.log('message.originatingEntity', message.originatingEntity);
          }

          if (message.originatingEntity === 'Bot') {
            this.pushSystem(message.text);
          } else {
            this.pushAgent(message.text);
          }
        }
      });
    });
    window.Genesys('subscribe', 'MessagingService.sendingMessage', function ({ message }: MessagesSendEvent) {
      if (!environment.production) {
        console.log('sendingMessage: ', message);
      }
    });
    window.Genesys('subscribe', 'MessagingService.typingReceived', ({ data }: TypingReceivedEvent) => {
      if (data.typing.type === 'On') {
        this._isTyping$.next(true);

        if (this.typingTimer) {
          clearTimeout(this.typingTimer);
        }
        this.typingTimer = setTimeout(() => {
          this._isTyping$.next(false);
        }, data.typing.durationMs);
      } else {
        this._isTyping$.next(false);
        if (this.typingTimer) {
          clearTimeout(this.typingTimer);
        }
      }
    });
    window.Genesys(
      'subscribe',
      'MessagingService.conversationDisconnected',
      ({ data }: ConversationDisconnectedEvent) => {
        if (!environment.production) {
          console.log('data:', data);
        }

        if (this.isChatStarted) {
          this.pushState('genesysChat.messages.chat.close');
          this._startedFLG$.next(false);
        }
      }
    );

    window.Genesys('subscribe', 'MessagingService.oldMessages', (payload: OldMessagesPayload) => {
      const messages: GenesysMessage[] = payload.data;
      if (!environment.production) {
        console.log(messages);
      }
    });
  }

  openChat(): void {
    if (!window.isChatOpened) {
      this.setChatAttributes();
      window.Genesys('command', 'Messenger.open');
    }
  }

  private startConversation(): void {
    window.Genesys(
      'command',
      'MessagingService.startConversation',
      {},
      function () {
        /*fulfilled callback*/
        if (!environment.production) {
          console.log('startConversation');
        }
        window.isChatOpened = true;
      },
      function () {
        /*rejected callback*/
      }
    );
    window.Genesys(
      'command',
      'MessagingService.fetchHistory',
      {},
      function () {
        /*fulfilled callback*/
      },
      function () {
        /*rejected callback*/
      }
    );
  }

  /// Helpers

  sendMessage(message: string): void {
    window.Genesys(
      'command',
      'MessagingService.sendMessage',
      {
        message: message,
      },
      function () {
        /*fulfilled callback*/

        if (!environment.production) {
          console.log('sendMessage', message);
        }
      },
      function () {
        /*rejected callback*/

        if (!environment.production) {
          console.log('err sending:');
        }
      }
    );
  }
  private pushState(text: string): void {
    this.push({
      type: 'state',
      text: text,
      time: this.now(),
      direction: 'Inbound',
    });
  }

  private pushSystem(text: string): void {
    this.push({
      type: 'system',
      text: text,
      time: this.now(),
      direction: '',
    });
  }

  private pushAgent(text: string): void {
    this.push({
      type: 'agent',
      text: text,
      time: this.now(),
      direction: 'Inbound',
    });
  }

  public pushUserMessage(text: string): void {
    this.push({
      type: 'user',
      text: text,
      time: this.now(),
      direction: 'Inbound',
    });
  }

  public pushNotAvailableMessage(text: string): void {
    this.push({
      type: 'system',
      text: text,
      time: this.now(),
      direction: 'Inbound',
    });
  }

  private push(m: ChatMessage): void {
    this._messages$.next([...this._messages$.value, m]);
  }

  private now(): string {
    const date = new Date();
    const dateStr = date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${dateStr} ${timeStr}`;
  }
}

type ChatCustomAttribute = {
  name?: string;
  queue: string;
};

export interface ConversationDisconnectedEvent {
  data: {
    message: Record<string, unknown>;
    readOnly: boolean;
  };
}
